/**
 * LE PIED DE L'ASSISTANT, À LA SYNTHÈSE — LA DÉCISION (11/10/2026).
 *
 * « Le bouton Finaliser la nuit est mal placé et pas mis en valeur »
 * (l'exploitant). Il était au bas du sixième panneau de la synthèse, à deux
 * écrans et demi sous la ligne de flottaison, pendant que le pied collant
 * — le seul emplacement principal visible à toute hauteur et à toute
 * largeur — affichait un « Suivant » grisé. Le geste est désormais LÀ :
 *
 *   à droite (`DecisionCloture`)  le geste que l'état de la clôture permet ;
 *   au centre (`EtatDuPied`)      la phrase qui dit où l'on en est.
 *
 * Jamais de bouton principal mort : avec des points bloquants, le geste
 * devient « Voir ce qui bloque » ; sans le droit, un lien vers la fiche de
 * la nuit. Une fois la nuit finalisée, la fiche de la nuit en bouton
 * principal — un lien, jamais une navigation forcée.
 */

import type { ReactNode } from 'react'
import {
  AlertTriangle, ArrowDown, ArrowRight, BadgeCheck, Check, CheckCircle2, Loader2, Lock, X,
} from 'lucide-react'
import { Link } from '../router.js'
import { Modal } from '../admin/primitives.js'
import { motionLevel } from '../shell/auth-motion.js'
import { formatMoney } from './money.js'
import { MotifField, ilYA, motifSuffisant, nuitLongue, type ClosingApi } from './ClosingCommon.js'
import { ValidationRemplacee, titreRemplacement } from './ValidationRemplacee.js'
import { chiffreOfficiel, recapFinalisation, ventilationDuChiffre } from './chiffre-officiel.js'
import type { GesteCloture } from './ClosingStepSummary.js'
import './finance-finalisation.css'

/** Le geste de droite, à la synthèse. */
export function DecisionCloture(props: { api: ClosingApi; geste: GesteCloture }) {
  const { api, geste } = props
  const d = api.closing
  const fiche = `/comptabilite/nuits/${d.business_date}`
  const bloquants = d.blockers.length

  const voirCeQuiBloque = () => {
    const el = document.getElementById('fiw-bloquants')
    if (!el) return
    el.scrollIntoView({ block: 'center', behavior: motionLevel() === 'full' ? 'smooth' : 'auto' })
    el.focus({ preventScroll: true })
  }
  const boutonBloque = (
    <button type="button" className="lc-btn fdc-bloque" onClick={voirCeQuiBloque}>
      Voir ce qui bloque
      <span className="lc-pill lc-pill--warning fdc-compte">{bloquants}</span>
      <ArrowDown size={16} aria-hidden="true" />
    </button>
  )

  let action: ReactNode
  if (d.status === 'CLOSED') {
    action = (
      <Link className="lc-btn lc-btn--primary" href={fiche}>
        Fiche de la nuit<ArrowRight size={16} aria-hidden="true" />
      </Link>
    )
  } else if (d.status === 'SUPERSEDED') {
    action = <Link className="lc-btn" href={fiche}>Fiche de la nuit</Link>
  } else if (d.status === 'READY' && bloquants > 0 && api.peut('finance.closure.close')) {
    // Prête, puis un obstacle (préventes pas encore connues, reddition à
    // vérifier) : pas de bouton qui mènerait à un refus — on mène à la raison.
    action = boutonBloque
  } else if (d.status === 'READY') {
    action = api.peut('finance.closure.close') ? (
      <button
        key={d.status}
        type="button"
        className="lc-btn lc-btn--primary lc-btn--lg fdc-cta"
        disabled={geste.occupe}
        onClick={() => void geste.finaliser()}
      >
        {/* L'appel : un anneau violet, une seule fois, à l'arrivée du geste. */}
        <span className="fdc-cta__appel" aria-hidden="true" />
        {geste.occupe
          ? <><Loader2 size={18} className="lc-spin" aria-hidden="true" />Finalisation en cours</>
          : <><Lock size={18} aria-hidden="true" />Finaliser la nuit</>}
      </button>
    ) : (
      <Link className="lc-btn lc-btn--ghost" href={fiche}>Fiche de la nuit</Link>
    )
  } else if (!api.peut('finance.closure.edit')) {
    // Le droit d'abord : à qui ne peut rien modifier, « Voir ce qui bloque »
    // promettait un geste qu'il ne pourra pas faire (le panneau liste quand
    // même les blocages, pour tous).
    action = <Link className="lc-btn lc-btn--ghost" href={fiche}>Fiche de la nuit</Link>
  } else if (bloquants > 0) {
    action = boutonBloque
  } else {
    action = (
      <button
        key={d.status}
        type="button"
        className="lc-btn lc-btn--primary lc-btn--lg"
        disabled={geste.occupe}
        onClick={() => void geste.marquerPrete()}
      >
        {geste.occupe
          ? <><Loader2 size={18} className="lc-spin" aria-hidden="true" />Vérification en cours</>
          : <><BadgeCheck size={18} aria-hidden="true" />Déclarer la clôture prête</>}
      </button>
    )
  }

  return (
    <>
      <span className="fdc-geste" ref={geste.ctaRef}>{action}</span>
      <ConfirmerRemplacement api={api} geste={geste} />
    </>
  )
}

/**
 * LA CONFIRMATION MOTIVÉE (16/10/2026) — quand finaliser ferait baisser une
 * nuit validée au-delà du seuil des Règles. Pas la confirmation simple : un
 * motif, parce que c'est la seule trace qui dira dans un an pourquoi le
 * chiffre de la nuit a baissé. La phrase est celle du serveur, la
 * conséquence d'abord.
 */
function ConfirmerRemplacement(props: { api: ClosingApi; geste: GesteCloture }) {
  const { api, geste } = props
  const d = api.closing
  const r = d.validation_replacement ?? null
  const ouvert = geste.remplacement.ouvert && r !== null
  // Le même récapitulatif que la confirmation simple : la nuit qui baisse
  // mérite au moins autant d'information que l'ordinaire.
  const recap = recapFinalisation(d)
  return (
    <Modal open={ouvert} onClose={geste.fermerRemplacement} ariaLabel="Remplacer la validation de la nuit" width={560} owning>
      <header className="lc-modal__head">
        <span className="lc-modal__icon" aria-hidden="true"><AlertTriangle size={22} /></span>
        <div>
          <div className="lc-modal__title">{r !== null ? titreRemplacement(r) : 'Finaliser la nuit'}</div>
          <div className="lc-modal__pills">
            <span className="lc-pill lc-pill--idle">{nuitLongue(d.business_date)}</span>
          </div>
        </div>
        <button className="lc-btn lc-btn--ghost lc-btn--icon lc-modal__close" onClick={geste.fermerRemplacement} aria-label="Fermer">
          <X size={18} />
        </button>
      </header>
      <div className="lc-modal__body fiw-modal">
        {r !== null ? <ValidationRemplacee r={r} /> : null}
        <div>
          <p className="lc-confirm__message">{recap.message}</p>
          <dl className="lc-confirm__details">
            {recap.details.map((x) => <div key={x.label}><dt>{x.label}</dt><dd>{x.value}</dd></div>)}
          </dl>
        </div>
        <MotifField
          id="fdc-motif-remplacement"
          valeur={geste.remplacement.motif}
          onChange={geste.setMotifRemplacement}
          label="Pourquoi remplacer la validation par ce chiffre ?"
          aide="Ce motif reste attaché à la finalisation de la nuit, pour toujours."
        />
        <div className="fiw-modal__actions">
          <button className="lc-btn lc-btn--ghost" onClick={geste.fermerRemplacement}>Ne pas finaliser</button>
          <button className="lc-btn lc-btn--danger" disabled={geste.occupe || !motifSuffisant(geste.remplacement.motif)}
            onClick={() => void geste.finaliserMalgreLaBaisse()}>
            <Lock size={16} aria-hidden="true" />Finaliser quand même
          </button>
        </div>
      </div>
    </Modal>
  )
}

/**
 * La phrase du centre, à TOUTES les étapes. L'ordre compte :
 *   1. une clôture figée le dit (et n'affiche plus « Brouillon enregistré »
 *      après une finalisation) ;
 *   2. une écriture en cours ;
 *   3. à la synthèse, l'état de la décision ; ailleurs, une clôture prête
 *      ramène à la synthèse d'un clic ;
 *   4. le dernier enregistrement ;
 *   5. la phrase de toujours.
 */
export function EtatDuPied(props: {
  api: ClosingApi
  courante: string
  sauvegarde: 'repos' | 'encours' | 'ok'
  onSynthese: () => void
}) {
  const { api, courante, sauvegarde, onSynthese } = props
  const d = api.closing
  const figee = d.status === 'CLOSED' || d.status === 'SUPERSEDED'

  if (figee) {
    return d.status === 'CLOSED' ? (
      <><Check size={15} className="fdc-ico--ok" aria-hidden="true" />Finalisée {ilYA(d.closed_at)}{d.closed_by_name ? ` par ${d.closed_by_name}` : ''}</>
    ) : (
      <>Révision remplacée · lecture seule</>
    )
  }
  if (sauvegarde === 'encours') {
    return <><Loader2 size={15} className="lc-spin" aria-hidden="true" />Enregistrement en cours</>
  }
  if (courante === 'synthese') {
    // LE TOTAL QUE LA FINALISATION ARRÊTE (11/10/2026) : sur place + préventes
    // reversées par Shotgun, et sa ventilation. Un serveur d'avant ne le sert
    // pas : le retenu, comme avant.
    const chiffre = chiffreOfficiel(d)
    const ventilation = ventilationDuChiffre(chiffre)
    const arrete = !chiffre.servi
      ? `${formatMoney(d.totals.retained_cents)} retenus`
      : chiffre.total !== null
        ? `${formatMoney(chiffre.total)} arrêtés`
        : `${formatMoney(chiffre.surPlace)} sur place + préventes`
    const detail = ventilation !== null ? <span className="fdc-detail">{ventilation}</span> : null
    // UNE NUIT VALIDÉE PLUS HAUT (16/10/2026) : à côté du bouton, jamais un
    // vert « prête » qui tait la baisse — à 390 px le bloc ambré de la
    // synthèse est loin au-dessus. Seulement quand la baisse est établie.
    const r = d.validation_replacement
    const baisse = r?.requires_confirmation === true && r.closing_known
      ? <span className="fdc-detail">{r.drop_cents !== null
          ? `Finaliser fera baisser la nuit de ${formatMoney(r.drop_cents)} · motif demandé`
          : 'Finaliser fera baisser le chiffre validé de la nuit · motif demandé'}</span>
      : null
    if (d.status === 'READY') {
      // Un obstacle peut réapparaître après « prête » (une reddition écartée,
      // une synchro) : la phrase le dit au lieu d'annoncer un chiffre.
      if (d.blockers.length > 0 && api.peut('finance.closure.close')) {
        const k = d.blockers.length
        return <><AlertTriangle size={15} className="fdc-ico--alerte" aria-hidden="true" />Prête, mais {k} point{k > 1 ? 's' : ''} à régler avant de finaliser</>
      }
      if (baisse !== null && api.peut('finance.closure.close')) {
        return (
          <>
            <span className="fdc-fort"><AlertTriangle size={15} className="fdc-ico--alerte" aria-hidden="true" />Prête · {arrete}, plus bas que la validation</span>
            {baisse}
          </>
        )
      }
      return api.peut('finance.closure.close') ? (
        <>
          <span className="fdc-fort"><CheckCircle2 size={15} className="fdc-ico--ok" aria-hidden="true" />Prête à finaliser · {arrete}</span>
          {detail}
        </>
      ) : (
        <>Prête · votre rôle ne permet pas de la finaliser</>
      )
    }
    if (!api.peut('finance.closure.edit')) return <>Votre rôle ne permet pas de modifier cette clôture</>
    const n = d.blockers.length
    if (n > 0) {
      return (
        <><AlertTriangle size={15} className="fdc-ico--alerte" aria-hidden="true" />{n} point{n > 1 ? 's' : ''} à régler avant de finaliser</>
      )
    }
    if (baisse !== null) {
      return (
        <>
          <span className="fdc-fort fdc-fort--calme"><AlertTriangle size={15} className="fdc-ico--alerte" aria-hidden="true" />Rien ne bloque · {chiffre.servi ? `${arrete} à la finalisation` : `retenu ${formatMoney(d.totals.retained_cents)}`}</span>
          {baisse}
        </>
      )
    }
    return (
      <>
        <span className="fdc-fort fdc-fort--calme"><CheckCircle2 size={15} className="fdc-ico--ok" aria-hidden="true" />Rien ne bloque · {chiffre.servi ? `${arrete} à la finalisation` : `retenu ${formatMoney(d.totals.retained_cents)}`}</span>
        {detail}
      </>
    )
  }
  if (d.status === 'READY') {
    return (
      <button type="button" className="lc-btn lc-btn--ghost lc-btn--sm" onClick={onSynthese}>
        Prête à finaliser<ArrowRight size={15} aria-hidden="true" />
      </button>
    )
  }
  if (sauvegarde === 'ok') {
    return <><Check size={15} aria-hidden="true" />Brouillon enregistré</>
  }
  return <>Chaque saisie est enregistrée au fil de l’eau</>
}
